import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router';
import { z } from 'zod';
import { KeyRound, MonitorSmartphone, Store } from 'lucide-react';
import { BRAND } from '@shripos/shared/brand';
import { forgotPasswordSchema, loginSchema, password as passwordRule } from '@shripos/shared/schemas';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  applyServerErrors,
  errorAt,
  takeTokenFromHash,
  useToast,
} from '@shripos/ui';
import { api } from '../../lib/api.js';
import { useSession } from '../../stores/session.js';

export function AuthShell({ title, subtitle, children, footer, wide }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-gradient-to-b from-brand-50 to-stone-100 px-4 py-10">
      <div className={wide ? 'w-full max-w-3xl' : 'w-full max-w-md'}>
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <img src="/favicon.svg" alt="" className="size-11" />
          <span className="text-2xl font-bold tracking-tight text-slate-900">{BRAND.productName}</span>
        </div>
        <Card className="p-6 sm:p-8">
          {title && <h1 className="text-xl font-semibold text-slate-900">{title}</h1>}
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          <div className={title ? 'mt-6' : ''}>{children}</div>
        </Card>
        {footer && <div className="mt-5 flex flex-col items-center gap-2 text-sm">{footer}</div>}
      </div>
    </div>
  );
}

const NOTICES = {
  sessionEnded: { tone: 'info', text: 'Your session ended. Please sign in again.' },
  licenseExpired: { tone: 'warning', key: 'license.expiredStaff' },
  deviceRevoked: { tone: 'warning', key: 'device.revoked' },
};

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const device = useSession((s) => s.device);
  const notice = NOTICES[useSession((s) => s.notice)];
  const [error, setError] = useState(null);
  const { register, handleSubmit, formState } = useForm({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      const res = await api.post('/auth/login', values);
      useSession.getState().signedIn(res.me);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err.code === 'LICENSE_EXPIRED'
          ? t('license.expiredStaff')
          : err.code === 'LICENSE_SUSPENDED'
            ? t('license.suspended')
            : err.message,
      );
    }
  });

  return (
    <AuthShell
      title={t('auth.signInTitle', { product: BRAND.productName })}
      subtitle={t('auth.signInSubtitle')}
      footer={
        device ? (
          <Link to="/pin" className="inline-flex items-center gap-1.5 font-medium text-brand-700 hover:underline">
            <KeyRound className="size-4" /> {t('auth.usePin')}
          </Link>
        ) : (
          <Link to="/activate" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-brand-700">
            <MonitorSmartphone className="size-4" /> {t('auth.activateDevice')}
          </Link>
        )
      }
    >
      {notice && (
        <Alert tone={notice.tone} className="mb-4">
          {notice.key ? t(notice.key) : notice.text}
        </Alert>
      )}
      {error && (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      )}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label={t('auth.email')} error={errorAt(formState.errors, 'email')}>
          <Input type="email" size="lg" autoComplete="username" autoFocus {...register('email')} />
        </Field>
        <Field label={t('auth.password')} error={errorAt(formState.errors, 'password')}>
          <Input type="password" size="lg" autoComplete="current-password" {...register('password')} />
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
          {t('auth.signIn')}
        </Button>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to="/forgot-password" className="text-brand-700 hover:underline">
          {t('auth.forgot')}
        </Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [done, setDone] = useState(null);
  const { register, handleSubmit, formState } = useForm({ resolver: zodResolver(forgotPasswordSchema) });
  const onSubmit = handleSubmit(async (values) => {
    const res = await api.post('/auth/forgot-password', values).catch((e) => ({ message: e.message }));
    setDone(res.message);
  });
  return (
    <AuthShell
      title={t('auth.forgotTitle')}
      subtitle={t('auth.forgotSubtitle')}
      footer={
        <Link to="/login" className="text-brand-700 hover:underline">
          {t('auth.backToSignIn')}
        </Link>
      }
    >
      {done ? (
        <Alert tone="success">{done}</Alert>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Field label={t('auth.email')} error={errorAt(formState.errors, 'email')}>
            <Input type="email" size="lg" autoFocus {...register('email')} />
          </Field>
          <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
            {t('auth.sendLink')}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

function newPasswordForm(t) {
  return z
    .object({ password: passwordRule, confirm: z.string() })
    .refine((v) => v.password === v.confirm, { message: t('auth.mismatch'), path: ['confirm'] });
}

/** /reset-password and /set-password (welcome + staff invites). Token arrives in the URL fragment. */
export function PasswordLinkPage({ mode }) {
  const { t } = useTranslation();
  const [token] = useState(() => takeTokenFromHash());
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const {
    register,
    handleSubmit,
    formState,
    setError: setFieldError,
  } = useForm({ resolver: zodResolver(newPasswordForm(t)) });

  const onSubmit = handleSubmit(async ({ password }) => {
    setError(null);
    try {
      await api.post(mode === 'set' ? '/auth/set-password' : '/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      applyServerErrors(setFieldError, err);
      setError(err.message);
    }
  });

  return (
    <AuthShell title={t(mode === 'set' ? 'auth.setTitle' : 'auth.resetTitle')} subtitle={t('auth.passwordHint')}>
      {!token && !done ? (
        <Alert tone="danger">{t('auth.linkMissing')}</Alert>
      ) : done ? (
        <>
          <Alert tone="success">{t('auth.passwordSaved')}</Alert>
          <Link to="/login">
            <Button size="lg" className="mt-4 w-full">
              {t('auth.signIn')}
            </Button>
          </Link>
        </>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label={t('auth.newPassword')} error={errorAt(formState.errors, 'password')}>
            <Input type="password" size="lg" autoComplete="new-password" autoFocus {...register('password')} />
          </Field>
          <Field label={t('auth.confirmPassword')} error={errorAt(formState.errors, 'confirm')}>
            <Input type="password" size="lg" autoComplete="new-password" {...register('confirm')} />
          </Field>
          <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
            {t('auth.savePassword')}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

/** Forced (temporary password) or voluntary password change. */
export function ChangePasswordPage({ embedded = false }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const me = useSession((s) => s.me);
  const schema = z
    .object({ currentPassword: z.string().min(1), newPassword: passwordRule, confirm: z.string() })
    .refine((v) => v.newPassword === v.confirm, { message: t('auth.mismatch'), path: ['confirm'] });
  const { register, handleSubmit, formState, setError, reset } = useForm({ resolver: zodResolver(schema) });
  const onSubmit = handleSubmit(async ({ currentPassword, newPassword }) => {
    try {
      const updated = await api.post('/auth/change-password', { currentPassword, newPassword });
      useSession.getState().setMe(updated);
      reset();
      toast.success(t('auth.changed'));
      if (!embedded) navigate('/', { replace: true });
    } catch (err) {
      applyServerErrors(setError, err);
      if (!err.fields?.currentPassword) toast.error(err);
    }
  });
  const form = (
    <form onSubmit={onSubmit} className="max-w-md space-y-4" noValidate>
      {me?.user?.mustChangePassword && <Alert tone="warning">{t('auth.mustChange')}</Alert>}
      <Field label={t('auth.currentPassword')} error={errorAt(formState.errors, 'currentPassword')}>
        <Input type="password" autoComplete="current-password" {...register('currentPassword')} />
      </Field>
      <Field
        label={t('auth.newPassword')}
        error={errorAt(formState.errors, 'newPassword')}
        hint={t('auth.passwordHint')}
      >
        <Input type="password" autoComplete="new-password" {...register('newPassword')} />
      </Field>
      <Field label={t('auth.confirmPassword')} error={errorAt(formState.errors, 'confirm')}>
        <Input type="password" autoComplete="new-password" {...register('confirm')} />
      </Field>
      <Button type="submit" loading={formState.isSubmitting}>
        {t('auth.savePassword')}
      </Button>
    </form>
  );
  if (embedded) {
    return (
      <>
        <h1 className="mb-5 text-xl font-semibold">{t('auth.changeTitle')}</h1>
        <Card className="p-5">{form}</Card>
      </>
    );
  }
  return <AuthShell title={t('auth.changeTitle')}>{form}</AuthShell>;
}

export function SelectOutletPage() {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  if (me.device) return <Navigate to="/" replace />;
  const pick = async (outletId) => {
    setBusy(outletId);
    try {
      const updated = await api.post('/auth/outlet', { outletId });
      useSession.getState().setMe(updated);
      navigate('/', { replace: true });
    } finally {
      setBusy(null);
    }
  };
  return (
    <AuthShell title={t('shell.selectOutlet')} subtitle={t('shell.selectOutletSubtitle')}>
      <div className="grid gap-3">
        {me.outlets.map((o) => (
          <button
            key={o.id}
            disabled={Boolean(busy)}
            onClick={() => pick(o.id)}
            className="flex items-center gap-3 rounded-xl p-4 text-left ring-1 ring-slate-200 transition hover:bg-brand-50 hover:ring-brand-300 disabled:opacity-50"
          >
            <Store className="size-6 text-brand-600" />
            <span>
              <span className="block font-semibold">{o.name}</span>
              <span className="font-mono text-xs text-slate-500">{o.code}</span>
            </span>
          </button>
        ))}
      </div>
    </AuthShell>
  );
}
